import React, { useEffect } from 'react';

type NoticeType = 'success' | 'error' | 'info' | 'warning';

export interface InlineNoticeProps {
  type?: NoticeType;
  message: string;
  details?: string[];
  onClose?: () => void;
  autoDismissMs?: number; // if provided, auto-dismiss after
  className?: string;
}

const palette: Record<NoticeType, { bg: string; border: string; text: string }> = {
  success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
  error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
  warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800' },
};

export const InlineNotice: React.FC<InlineNoticeProps> = ({
  type = 'info',
  message,
  details,
  onClose,
  autoDismissMs,
  className,
}) => {
  useEffect(() => {
    if (!autoDismissMs || !onClose) return;
    const t = setTimeout(() => onClose(), autoDismissMs);
    return () => clearTimeout(t);
  }, [autoDismissMs, onClose]);

  const theme = palette[type];

  return (
    <div className={`p-3 rounded-lg border ${theme.bg} ${theme.border} ${theme.text} ${className ?? ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">{message}</div>
          {details && details.length > 0 && (
            <ul className="mt-1 text-xs list-disc pl-4">
              {details.map((d, i) => (
                <li key={`${i}-${d}`} className="truncate">{d}</li>
              ))}
            </ul>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} className="text-sm opacity-70 hover:opacity-100" aria-label="Dismiss notice">
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

export default InlineNotice;

